import React, { useState, useEffect } from "react";
import { get } from "../../services/smartApiService";
import { DASHBOARD_URLS } from "../../api/DashboardUrls";
import DashboardSummary from "./components/DashboardSummary";
import RecentAlerts from "./components/RecentAlerts";
import RecentPatterns from "./components/RecentPatterns";
import SectorPerformance from "./components/SectorPerformance";
import MarketMovers from "./components/MarketMovers";
import IndexOverview from "./components/IndexOverview";
import AlertDetailModal from "./components/AlertDetailModal";
import "./DashboardHome.scss";
import SectorTable from "../Manage/SectorTable";

interface DashboardData {
  summary: any;
  alerts: any[];
  patterns: any[];
  sectors: any[];
  movers: any;
  indices: any[];
}

const DashboardHome: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    summary: null,
    alerts: [],
    patterns: [],
    sectors: [],
    movers: null,
    indices: [],
  });
  const [selectedAlert, setSelectedAlert] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    loadDashboardData();
  }, [refreshKey]);

  const loadDashboardData = () => {
    setLoading(true);

    // Load Summary
    get(DASHBOARD_URLS.SUMMARY, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, summary: response.data.data }));
      },
      error: (err) => console.error("Failed to load summary:", err),
    });

    // Load Recent Alerts
    get(`${DASHBOARD_URLS.ALERTS_RECENT}?limit=10`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, alerts: response.data.data || [] }));
      },
      error: (err) => console.error("Failed to load alerts:", err),
    });

    // Load Recent Patterns
    get(`${DASHBOARD_URLS.PATTERNS_RECENT}?limit=20`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, patterns: response.data.data || [] }));
      },
      error: (err) => console.error("Failed to load patterns:", err),
    });

    // Load Sector Performance
    get(DASHBOARD_URLS.SECTOR_PERFORMANCE, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, sectors: response.data.data || [] }));
      },
      error: (err) => console.error("Failed to load sectors:", err),
    });

    // Load Market Movers
    get(`${DASHBOARD_URLS.MARKET_MOVERS}?type=gainers&limit=5`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, movers: response.data.data || [] }));
        setLoading(false);
      },
      error: (err) => {
        console.error("Failed to load market movers:", err);
        setLoading(false);
      },
    });

    // Load Indices Overview
    get(DASHBOARD_URLS.INDICES_OVERVIEW, { showLoading: false }).subscribe({
      next: (response: any) => {
        setDashboardData((prev) => ({ ...prev, indices: response.data.data || [] }));
      },
      error: (err) => console.error("Failed to load indices:", err),
    });
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleAlertClick = (alertId: number) => {
    setSelectedAlert(alertId);
  };

  const handleCloseModal = () => {
    setSelectedAlert(null);
  };

  return (
    <div className="dashboard-home">
      <div className="container is-fluid">
  
        <div className="dashboard-header">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title is-3">Market Dashboard</h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <button className="button is-primary" onClick={handleRefresh}>
                  <span className="icon">
                    <i className="fa fa-refresh"></i>
                  </span>
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && !dashboardData.summary ? (
          <div className="has-text-centered p-6">
            <div className="loader is-loading"></div>
            <p>Loading dashboard data...</p>
          </div>
        ) : (
          <>

            <DashboardSummary summary={dashboardData.summary} />


            <IndexOverview indices={dashboardData.indices} />

            <div className="columns">
        
              <div className="column is-8">
         
                <RecentAlerts
                  alerts={dashboardData.alerts}
                  onAlertClick={handleAlertClick}
                />

          
                <RecentPatterns patterns={dashboardData.patterns} />
              </div>

        
              <div className="column is-4">
    
                <MarketMovers movers={dashboardData.movers} />

         
                <SectorPerformance sectors={dashboardData.sectors} />
              </div>
            </div>
          </>
        )}
      </div>

 
      {selectedAlert && (
        <AlertDetailModal alertId={selectedAlert} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default DashboardHome;
